import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CloseIcon,
	Box,
	Grid,
	TextField,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	FormControlLabel,
	FormGroup,
	Autocomplete,
	Input,
	Checkbox,
	ListItemText,
	FormLabel,
	CircularProgress,
	InputAdornment,
	IconButton,
	Visibility,
	VisibilityOff,
	Radio,
	RadioGroup,
	Tooltip,
} from "../../../common/MaterialComponent";
import "./CountryDialog.css";
import CountryModel from "../../../models/Country/CountryModel";
import { AddUpdateCountryDetail } from "../../../api/countryController";
import {
	VoiceConnectivityMethod,
	AlphanumericSupport,
	ServiceProvider,
} from "../../../common/enum";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import Shared from "../../../common/shared";
import {
	IconButtonPrimary,
	primaryColor,
	LinkPrimary,
	ButtonPrimary,
} from "../../../common/sharedStyle";
import { allCountryCodes as countryCodeList } from "../../../common/countryCodes";
import { ICountryCode } from "../../../common/interfaces";
import HelpIcon from "@material-ui/icons/Help";
// import { containerSizesSelector } from "@material-ui/data-grid";

export interface DialogProps {
	open: boolean;
	closeDialog: (country?: CountryModel) => void;
	countryDetail: CountryModel;
}

interface ICountryFlag {
	flagUrl: string;
	countryName: string;
}

interface IDropdownType {
	value: AlphanumericSupport | VoiceConnectivityMethod | string;
	description: string;
}

export interface DialogState {
	countryFlagList: ICountryFlag[];
	selectedCountry: CountryModel;
	isLoading: boolean;
	isAddCountryRequest: boolean;
	isAuthTokenPasswordField: boolean;
}

export const CountryDialog: React.FC<DialogProps> = ({
	open,
	closeDialog,
	countryDetail,
}) => {
	const [projectName, setProjectName] = useState("");
	const [countryNameValue, setCountryNameValue] = useState("");
	const [countryFlagList, setCountryFlagList] = useState<ICountryFlag[]>([]);
	const [selectedCountry, setSelectedCountry] = useState(countryDetail);
	const [isLoading, setIsLoading] = useState(false);
	const [isAddCountryRequest, setIsAddCountryRequest] = useState(
		countryDetail.id ? false : true
	);
	const [
		isAuthTokenPasswordField,
		setIsAuthTokenPasswordField,
	] = useState<boolean>(false);
	const strings = useGetComponentStringsContext().CountryDialog;

	useEffect(() => {
		const flagList: ICountryFlag[] = [];
		const keyList: string[] = countryCodeList.map((c) => c.code);
		const values: string[] = countryCodeList.map((c) => c.name);
		for (var i = 0; i < keyList.length; i++) {
			keyList[i] = keyList[i].toLowerCase();
			flagList.push({
				flagUrl: "images/flags/" + keyList[i] + ".png",
				countryName: values[i].toString(),
			});
		}
		setCountryFlagList(flagList);
	}, []);

	useEffect(() => {
		setSelectedCountry(() => ({
			...countryDetail,
			flagImageName: countryDetail.flagImageName
				? "images/flags/" + countryDetail.flagImageName
				: "",
		}));

		setIsAddCountryRequest(() => (countryDetail.id ? false : true));
		setIsAuthTokenPasswordField(countryDetail.twilioAuthToken ? true : false);
	}, [countryDetail]);

	const isWhatsappBlockedCountry = useMemo(() => {
		return Shared.whatsappBlockedCountries.includes(selectedCountry.id);
	}, [selectedCountry.id]);

	const onCountryFlagSelection = (event: any, values: any) => {
		const index =
			values && values.flagUrl
				? countryCodeList
						.map((c) => c.code)
						.indexOf(getCountryCodeFromURL(values.flagUrl))
				: -1;

		setSelectedCountry((prevState: CountryModel) => ({
			...prevState, // keep all other key-value pairs
			flagImageName: values ? values.flagUrl : "", // update the value of specific key
			isWhatsappEnabled: !isAddCountryRequest
				? prevState.isWhatsappEnabled
				: false,
			countryCodeISO: index > -1 ? countryCodeList[index].code : "",
		}));
		setCountryNameValue(values.countryName);
	};

	const getCountryCodeFromURL = (url: string) => {
		return url
			? url.includes("images/flags")
				? url
						.substr(url.lastIndexOf("/") + 1)
						.split(".")[0]
						.toUpperCase()
				: url.split(".")[0].toUpperCase()
			: "";
	};

	const onCountryCodeChange = (event: any, value: any) => {
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				countryCodeISO: value,
			};
		});
	};

	const onProjectNameChange = (event: any) => {
		setProjectName(event.target.value);
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				id: event.target.value,
			};
		});
	};

	const submitCountryDetail = async () => {
		if (!isValidForm()) {
			return;
		}

		if (isAddCountryRequest) {
			selectedCountry.id = countryNameValue.trim() + " " + projectName.trim();
		}

		const countryModel: CountryModel = Object.assign(new CountryModel(), {
			...selectedCountry,
		});

		const flagImageName = countryModel.flagImageName.split("/").pop();
		// if flag is not selected, set default country flag from countryCode
		countryModel.flagImageName = flagImageName
			? flagImageName
			: countryModel.countryCodeISO + ".png";
		if (!countryModel.isSMSEnabled) {
			countryModel.isOutboundSMS = false;
			countryModel.isInboundSMS = false;
			countryModel.isSenderIdPreserved = false;
			countryModel.alphanumericSupport = AlphanumericSupport.None;
		}
		if (!countryModel.isVoiceEnabled) {
			countryModel.voiceConnectivityMethod = VoiceConnectivityMethod.None;
		}
		switch (countryModel.serviceProviders) {
			case 0: {
				console.log("Service Chosen, Twilio");
				countryModel.africasTalkingApiKey = "";
				countryModel.africasTalkingUserName = "";
				countryModel.vodafoneApiBaseUrl = "";
				countryModel.vodafoneDistributionId = "";
				countryModel.vodafoneProviderPassword = "";
				countryModel.vodafoneProviderUserName = "";
				break;
			}
			case 1: {
				console.log("Service Chosen, Africas Talking ");
				countryModel.twilioAccountSid = "";
				countryModel.twilioAuthToken = "";
				countryModel.whatsappEnabledPhoneNumber = "";
				countryModel.vodafoneApiBaseUrl = "";
				countryModel.vodafoneDistributionId = "";
				countryModel.vodafoneProviderPassword = "";
				countryModel.vodafoneProviderUserName = "";
				break;
			}
			case 2: {
				console.log("Service Chosen, Vodafone");
				countryModel.africasTalkingApiKey = "";
				countryModel.africasTalkingUserName = "";
				countryModel.twilioAccountSid = "";
				countryModel.twilioAuthToken = "";
				countryModel.whatsappEnabledPhoneNumber = "";
				break;
			}
			default: {
				break;
			}
		}

		countryModel.isAddCountryRequest = isAddCountryRequest;
		setIsLoading(true);
		try {
			const res = await AddUpdateCountryDetail(countryModel);
			if (res.responseObj) {
				closeDialog(res.responseObj);
			}
			setIsLoading(false);
			Shared.showSnackbarMessage(
				res.responseObj
					? strings.saveCountrySuccessMessage
					: res.error
					? res.error
					: strings.saveCountryErrorMessage
			);
		} catch (err) {
			setIsLoading(false);
			Shared.showSnackbarMessage(strings.saveCountryErrorMessage);
		}
	};

	const onExclusionListChange = (
		event: React.ChangeEvent<{ value: unknown }>
	) => {
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				flowExclusionList: event.target.value as string[],
			};
		});
	};

	const onAlphanumericSupportValueChange = (
		event: React.ChangeEvent<{ value: unknown }>
	) => {
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				alphanumericSupport: event.target.value as AlphanumericSupport,
			};
		});
	};

	const onVoiceConnectivityChange = (
		event: React.ChangeEvent<{ value: unknown }>
	) => {
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				voiceConnectivityMethod: event.target.value as VoiceConnectivityMethod,
			};
		});
	};

	const onConnectivityValueChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const countryModel = Object.assign({}, selectedCountry);
		if (event.target.name === "sms") {
			countryModel.isSMSEnabled = event.target.checked;
		}
		if (event.target.name === "voice") {
			countryModel.isVoiceEnabled = event.target.checked;
		}
		if (event.target.name === "whatsapp") {
			countryModel.isWhatsappEnabled = event.target.checked;
		}
		if (event.target.name === "ussd") {
			countryModel.isUSSDEnabled = event.target.checked;
		}
		if (event.target.name === "outbound_sms") {
			countryModel.isOutboundSMS = event.target.checked;
		}
		if (event.target.name === "inbound_sms") {
			countryModel.isInboundSMS = event.target.checked;
		}
		if (event.target.name === "senderId_Preserved") {
			countryModel.isSenderIdPreserved = event.target.checked;
		}
		setSelectedCountry(countryModel);
	};
	const onIntegrationValueChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const countryModel = Object.assign({}, selectedCountry);
		if (event.target.name === "DCH") {
			countryModel.dchEnabled = event.target.checked;
		}
		setSelectedCountry(countryModel);
	};

	const onAccountSidValueChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, twilioAccountSid: event.target.value };
		});
	};

	const onAuthTokenValueChange = (event: any) => {
		setIsAuthTokenPasswordField(false);
		setSelectedCountry((prevState) => {
			return { ...prevState, twilioAuthToken: event.target.value };
		});
	};

	const onAzureResoureGroupNameValueChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, azureResourceGroupName: event.target.value };
		});
	};

	const onSharepointSiteIDValueChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, sharepointSiteId: event.target.value };
		});
	};

	const onSharepointDriveIDValueChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, sharepointDriveId: event.target.value };
		});
	};

	const getCountryFlagElementValue = () => {
		// get countryCode from url
		const countryCode = getCountryCodeFromURL(selectedCountry.flagImageName);
		const country: ICountryCode | undefined = countryCodeList.find(
			(c) => c.code === countryCode
		);
		const countryName = country ? country.name : "";
		return {
			flagUrl: selectedCountry.flagImageName,
			countryName: countryName,
		};
	};
	const alphanumericSupportList: IDropdownType[] = [
		{
			value: AlphanumericSupport.None,
			description: strings.noneText,
		},
		{
			value: AlphanumericSupport.RegistrationRequired,
			description: strings.registrationRequiredText,
		},
		{
			value: AlphanumericSupport.Dynamic,
			description: strings.dynamicText,
		},
	];
	const voiceConnectivityMethodList: IDropdownType[] = [
		{
			value: VoiceConnectivityMethod.None,
			description: strings.noneText,
		},
		{
			value: VoiceConnectivityMethod.TwilioPhoneNumber,
			description: strings.twilioPhoneNumberText,
		},
		{
			value: VoiceConnectivityMethod.TwilioSIPTrunking,
			description: strings.twilioSipTrunkingText,
		},
		{
			value: VoiceConnectivityMethod.Telerivet,
			description: strings.telerivetText,
		},
	];
	const flowExclusionList: IDropdownType[] = [
		{
			value: "Batch Messaging",
			description: strings.batchMessaging,
		},
		{
			value: "Requests for Help",
			description: strings.requestsForHelpText,
		},
		{
			value: "Surveys",
			description: strings.surveysText,
		},
	];

	const isValidForm = () => {
		return !selectedCountry.countryCodeISO ||
			!selectedCountry.id ||
			(!selectedCountry.africasTalkingEnabled &&
				selectedCountry.isWhatsappEnabled &&
				!selectedCountry.whatsappEnabledPhoneNumber)
			? false
			: true;
	};

	const toggleAuthTokenPasswordField = () => {
		setIsAuthTokenPasswordField(!isAuthTokenPasswordField);
	};

	const communicationViaOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const chosenService = parseInt(event.target.value);
		setIsAuthTokenPasswordField(
			countryDetail?.twilioAuthToken === selectedCountry.twilioAuthToken
				? true
				: false
		);

		const africasTalkingEnabled = chosenService === 1 ? true : false;
		setSelectedCountry((prevState) => {
			return {
				...prevState,
				africasTalkingEnabled: africasTalkingEnabled,
				isWhatsappEnabled: africasTalkingEnabled
					? false
					: !isAddCountryRequest
					? countryDetail.isWhatsappEnabled
					: prevState.isWhatsappEnabled,
				serviceProviders: chosenService,
				vodafoneProviderEnabled: chosenService === 2,
			};
		});
	};

	const onAfricasTalkingUserNameChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, africasTalkingUserName: event.target.value };
		});
	};

	const onAfricasTalkingApiKeyChange = (event: any) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, africasTalkingApiKey: event.target.value };
		});
	};

	const onWhatsAppEnabledPhoneNumberChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, whatsappEnabledPhoneNumber: event.target.value };
		});
	};

	const handleVodafoneValueChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const { name, value } = event.target;
		console.log(name, value);
		setSelectedCountry((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const onDCHAPIKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, dchApiKey: event.target.value };
		});
	};

	const onDCHURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, dchApiURL: event.target.value };
		});
	};

	const onDCHCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, dchCountry: event.target.value };
		});
	};

	const onDCHEnvironmentChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setSelectedCountry((prevState) => {
			return { ...prevState, dchEnvironment: event.target.value };
		});
	};
	return (
		<div data-testid="country-dialog">
			<Dialog fullWidth open={open}>
				<DialogTitle className="dialogTitle">
					<Box display="flex" alignItems="center">
						<Box flexGrow={1} className={primaryColor}>
							{isAddCountryRequest
								? `${strings.addCountryLabel} `
								: `${strings.editCountryLabel} `}
							{strings.countryDetailMessage}
						</Box>
						<Box>
							<IconButtonPrimary onClick={() => closeDialog()}>
								<CloseIcon />
							</IconButtonPrimary>
						</Box>
					</Box>
				</DialogTitle>
				<DialogContent className="dialogContent" dividers>
					<Grid container>
						{selectedCountry.flagImageName && (
							<Grid className="gridSpace" item xs={1}>
								<img
									className="selectedFlag"
									src={selectedCountry.flagImageName}
									alt="CountryFlag"
								/>
							</Grid>
						)}
						<Grid
							className="gridSpace"
							item
							xs={selectedCountry.flagImageName ? 11 : 12}
						>
							<Autocomplete
								onChange={onCountryFlagSelection}
								value={getCountryFlagElementValue()}
								getOptionSelected={(option, value) =>
									!value.flagUrl ||
									(option.flagUrl === value.flagUrl && !value.countryName) ||
									option.countryName === value.countryName
								}
								renderOption={(option) => {
									return (
										<div className="flagList" key={option.countryName}>
											<img
												className="flag"
												src={option.flagUrl}
												alt={option.countryName}
											/>
											<span className="countryNameSpan">
												{option.countryName}
											</span>
										</div>
									);
								}}
								getOptionLabel={(option) => option.countryName}
								options={countryFlagList}
								autoHighlight
								renderInput={(params) => (
									<span>
										<TextField
											{...params}
											value={params}
											label={strings.countryName}
										/>
									</span>
								)}
								disabled={!isAddCountryRequest}
							/>
						</Grid>
						<Grid className="gridSpace" item xs={12} sm={6}>
							<TextField
								fullWidth
								required
								label={strings.projectName}
								value={selectedCountry.id}
								onChange={onProjectNameChange}
								helperText={
									!selectedCountry.id ? strings.projectNameErrorText : ""
								}
								inputProps={{
									"data-testid": "projectName",
								}}
								disabled={!isAddCountryRequest}
							/>
						</Grid>
						<Grid className="gridSpace" item xs={12} sm={6}>
							<Autocomplete
								options={countryCodeList.map((c) => c.code)}
								onChange={onCountryCodeChange}
								autoHighlight
								value={selectedCountry.countryCodeISO}
								getOptionSelected={(option, value) =>
									!value || option === value
								}
								renderInput={(params) => (
									<TextField
										{...params}
										helperText={
											!selectedCountry.countryCodeISO
												? strings.countryCodeErrorText
												: ""
										}
										required
										label={strings.countryCode}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Box ml={1}>
								<RadioGroup
									row
									value={selectedCountry.serviceProviders}
									onChange={communicationViaOptionChange}
								>
									<FormControlLabel
										value={ServiceProvider.Twilio}
										control={<Radio color="primary" />}
										label={strings.twilioOptionLabel}
									/>
									<FormControlLabel
										value={ServiceProvider.AfricasTalking}
										control={<Radio color="primary" />}
										label={strings.africasTalkingOptionLabel}
									/>
									<FormControlLabel
										value={ServiceProvider.Vodafone}
										control={<Radio color="primary" />}
										label={strings.vodafoneOptionLabel}
									/>
								</RadioGroup>
							</Box>
						</Grid>
						{selectedCountry.serviceProviders === 0 && (
							<>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										label={strings.twilioAccountSid}
										value={selectedCountry.twilioAccountSid}
										onChange={onAccountSidValueChange}
										data-testid="twilioAccountSidInput"
									/>
								</Grid>
								<Grid className="gridSpace" item xs={12}>
									<FormControl fullWidth>
										<InputLabel>{strings.twilioAuthToken}</InputLabel>
										<Input
											data-testid="twilioAuthTokenInput"
											type={isAuthTokenPasswordField ? "password" : "text"}
											value={selectedCountry.twilioAuthToken}
											onChange={onAuthTokenValueChange}
											endAdornment={
												countryDetail.twilioAuthToken &&
												countryDetail.twilioAuthToken ===
													selectedCountry.twilioAuthToken && (
													<InputAdornment position="end">
														<IconButton onClick={toggleAuthTokenPasswordField}>
															{!isAuthTokenPasswordField ? (
																<Visibility />
															) : (
																<VisibilityOff />
															)}
														</IconButton>
													</InputAdornment>
												)
											}
										/>
									</FormControl>
								</Grid>
							</>
						)}
						{selectedCountry.serviceProviders === 1 && (
							<>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										label={strings.africasTalkingUserName}
										value={selectedCountry.africasTalkingUserName || ""}
										onChange={onAfricasTalkingUserNameChange}
										data-testid="africasTalkingUserNameInput"
									/>
								</Grid>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										label={strings.africasTalkingApiKey}
										value={selectedCountry.africasTalkingApiKey || ""}
										onChange={onAfricasTalkingApiKeyChange}
										data-testid="africasTalkingApiKeyInput"
									/>
								</Grid>
							</>
						)}
						{selectedCountry.serviceProviders === 2 && (
							<>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										name="vodafoneProviderUserName"
										label={strings.vodafoneProviderUsername}
										value={selectedCountry.vodafoneProviderUserName || ""}
										onChange={handleVodafoneValueChange}
										data-testid="vodafoneUserNameInput"
									/>
								</Grid>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										name="vodafoneProviderPassword"
										label={strings.vodafoneProviderPassword}
										value={selectedCountry.vodafoneProviderPassword || ""}
										onChange={handleVodafoneValueChange}
										data-testid="vodafoneProviderPasswordInput"
									/>
								</Grid>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										name="vodafoneDistributionId"
										label={strings.vodafoneDistributionId}
										value={selectedCountry.vodafoneDistributionId || ""}
										onChange={handleVodafoneValueChange}
										data-testid="vodafoneDistributionId"
									/>
								</Grid>
								<Grid className="gridSpace" item xs={12}>
									<TextField
										fullWidth
										name="vodafoneApiBaseUrl"
										label={strings.vodafoneApiBaseUrl}
										value={selectedCountry.vodafoneApiBaseUrl || ""}
										onChange={handleVodafoneValueChange}
										data-testid="vodafoneApiBaseUrl"
									/>
								</Grid>
							</>
						)}
						<Grid className="gridSpace" item xs={12}>
							<TextField
								fullWidth
								label={strings.azureResourceGroupName}
								value={selectedCountry.azureResourceGroupName}
								onChange={onAzureResoureGroupNameValueChange}
							/>
						</Grid>
						<Grid className="gridSpace" item xs={12}>
							<TextField
								fullWidth
								label={strings.sharepointSiteId}
								value={selectedCountry.sharepointSiteId}
								onChange={onSharepointSiteIDValueChange}
							/>
						</Grid>
						<Grid className="gridSpace" item xs={12}>
							<TextField
								fullWidth
								label={strings.sharepointDriveId}
								value={selectedCountry.sharepointDriveId}
								onChange={onSharepointDriveIDValueChange}
							/>
						</Grid>
						<Grid className="gridSpace" item xs={12}>
							<FormControl fullWidth>
								<InputLabel>{strings.exclusionList}</InputLabel>
								<Select
									value={selectedCountry.flowExclusionList}
									input={<Input />}
									onChange={onExclusionListChange}
									multiple
									renderValue={(selected) => (selected as string[]).join(", ")}
									endAdornment={
										<InputAdornment position="end" className="selectAdornment">
											<Tooltip
												title={strings.unsupportedTemplateInformation}
												classes={{
													tooltip: "infoTooltip",
												}}
											>
												<HelpIcon color="primary" />
											</Tooltip>
										</InputAdornment>
									}
									className="selectInput"
								>
									{flowExclusionList.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											<Checkbox
												color="primary"
												checked={
													selectedCountry.flowExclusionList.indexOf(
														(option as any).value
													) > -1
												}
											/>
											<ListItemText primary={option.description} />
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item className="gridSpace" xs={12} sm={12}>
							<FormLabel>{strings.connectivity}</FormLabel>
							<FormGroup>
								<span>
									<FormControlLabel
										control={
											<Checkbox
												color="primary"
												checked={selectedCountry.isSMSEnabled}
												onChange={onConnectivityValueChange}
												name="sms"
											/>
										}
										label={strings.smsLabel}
									/>
								</span>
								{selectedCountry.isSMSEnabled && (
									<Grid container className="connectivityConfiguration">
										<p>{strings.connectivityNoteMessage}</p>
										<LinkPrimary
											className="geoPermissionLink"
											href="https://www.twilio.com/console/sms/settings/geo-permissions"
											target="blank"
										>
											https://www.twilio.com/console/sms/settings/geo-permissions
										</LinkPrimary>

										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													name="outbound_sms"
													checked={selectedCountry.isOutboundSMS}
													onChange={onConnectivityValueChange}
												/>
											}
											label={strings.outboundSMSLabel}
											className="smsConnectivityChkBox"
										/>
										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													name="inbound_sms"
													checked={selectedCountry.isInboundSMS}
													onChange={onConnectivityValueChange}
												/>
											}
											label={strings.inboundSMSLabel}
											className="smsConnectivityChkBox"
										/>
										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													name="senderId_Preserved"
													checked={selectedCountry.isSenderIdPreserved}
													onChange={onConnectivityValueChange}
												/>
											}
											label={strings.senderIdPreservedLabel}
											className="smsConnectivityChkBox"
										/>
										<div className="alphaNumericSupport">
											<FormControl fullWidth>
												<InputLabel>
													{strings.alphaNumericSupportLabel}
												</InputLabel>
												<Select
													value={selectedCountry.alphanumericSupport}
													onChange={onAlphanumericSupportValueChange}
												>
													{alphanumericSupportList.map((option) => (
														<MenuItem key={option.value} value={option.value}>
															<ListItemText primary={option.description} />
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</div>
									</Grid>
								)}
								{selectedCountry.serviceProviders !== 2 && (
									<span>
										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													checked={selectedCountry.isVoiceEnabled}
													onChange={onConnectivityValueChange}
													name="voice"
												/>
											}
											label={strings.voiceLabel}
										/>
									</span>
								)}
								{selectedCountry.isVoiceEnabled && (
									<Grid className="connectivityConfiguration" container>
										<p>{strings.connectivityNoteMessage}</p>
										<LinkPrimary
											className="geoPermissionLink"
											href="https://www.twilio.com/console/voice/calls/geo-permissions"
											target="blank"
										>
											https://www.twilio.com/console/voice/calls/geo-permissions
										</LinkPrimary>
										<div className="alphaNumericSupport">
											<FormControl fullWidth>
												<InputLabel>
													{strings.voiceConnectivityMethodLabel}
												</InputLabel>
												<Select
													className="width50"
													value={selectedCountry.voiceConnectivityMethod}
													onChange={onVoiceConnectivityChange}
												>
													{voiceConnectivityMethodList.map((option) => (
														<MenuItem key={option.value} value={option.value}>
															<ListItemText primary={option.description} />
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</div>
									</Grid>
								)}
								{selectedCountry.serviceProviders !== 2 && (
									<span>
										<Tooltip
											title={
												isWhatsappBlockedCountry ||
												selectedCountry.africasTalkingEnabled
													? strings.whatsappBlockedCountryMessage
													: ""
											}
											classes={{
												tooltip: "infoTooltip",
											}}
										>
											<FormControlLabel
												control={
													<Checkbox
														data-testid="whatsappConnectivityRadioInput"
														color="primary"
														checked={selectedCountry.isWhatsappEnabled}
														onChange={onConnectivityValueChange}
														name="whatsapp"
														disabled={
															isWhatsappBlockedCountry ||
															selectedCountry.africasTalkingEnabled
														}
													/>
												}
												label={strings.whatsappLabel}
											/>
										</Tooltip>
									</span>
								)}
								{selectedCountry.isWhatsappEnabled && (
									<Grid className="connectivityConfiguration" container>
										<p>{strings.whatsappNoteMessage}</p>
										<LinkPrimary
											className="geoPermissionLink"
											href="https://www.twilio.com/docs/whatsapp/tutorial/connect-number-business-profile"
											target="blank"
										>
											https://www.twilio.com/docs/whatsapp/tutorial/connect-number-business-profile
										</LinkPrimary>
										<TextField
											fullWidth
											required
											label={strings.whatsappEnabledPhoneNumberLabel}
											value={selectedCountry.whatsappEnabledPhoneNumber || ""}
											onChange={onWhatsAppEnabledPhoneNumberChange}
											className="whatsappEnabledPhoneNumber"
											data-testid="whatsappEnabledPhoneNumberInput"
										/>
									</Grid>
								)}
								{selectedCountry.serviceProviders !== 2 && (
									<span>
										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													checked={selectedCountry.isUSSDEnabled}
													onChange={onConnectivityValueChange}
													name="ussd"
												/>
											}
											label={strings.ussdLabel}
										/>
									</span>
								)}
								{selectedCountry.isUSSDEnabled && (
									<Grid className="connectivityConfiguration" container>
										<p>{strings.ussdNoteAfricasTalkingLink}</p>
										<LinkPrimary
											className="geoPermissionLink"
											href="https://africastalking.com/ussd"
											target="blank"
										>
											https://africastalking.com/ussd
										</LinkPrimary>
										<p>{strings.ussdNoteTelerivetLink}</p>
										<LinkPrimary
											className="geoPermissionLink"
											href="https://telerivet.com/product/ussd"
											target="blank"
										>
											https://telerivet.com/product/ussd
										</LinkPrimary>
									</Grid>
								)}
							</FormGroup>
						</Grid>
						<Grid item className="gridSpace" xs={12} sm={12}>
							<FormLabel>{strings.intergrations}</FormLabel>
							<FormGroup>
								<span>
									<FormControlLabel
										control={
											<Checkbox
												color="primary"
												checked={selectedCountry.dchEnabled}
												onChange={onIntegrationValueChange}
												name="DCH"
											/>
										}
										label={strings.dchLabel}
									/>
								</span>
								{selectedCountry.dchEnabled && (
									<Grid container className="connectivityConfiguration">
										<p>{strings.dchMessage}</p>
										<TextField
											fullWidth
											required
											label={strings.dchApiKeyLabel}
											value={selectedCountry.dchApiKey || ""}
											onChange={onDCHAPIKeyChange}
											className="whatsappEnabledPhoneNumber"
											data-testid="whatsappEnabledPhoneNumberInput"
										/>
										<TextField
											fullWidth
											required
											label={strings.dchApiURLLabel}
											value={selectedCountry.dchApiURL || ""}
											onChange={onDCHURLChange}
											className="whatsappEnabledPhoneNumber"
											data-testid="whatsappEnabledPhoneNumberInput"
										/>
										<TextField
											fullWidth
											required
											label={strings.dchCountryLabel}
											value={selectedCountry.dchCountry || ""}
											onChange={onDCHCountryChange}
											className="whatsappEnabledPhoneNumber"
											data-testid="whatsappEnabledPhoneNumberInput"
										/>
										<TextField
											fullWidth
											required
											label={strings.dchEnvironmentLabel}
											value={selectedCountry.dchEnvironment || ""}
											onChange={onDCHEnvironmentChange}
											className="whatsappEnabledPhoneNumber"
											data-testid="whatsappEnabledPhoneNumberInput"
										/>
									</Grid>
								)}
							</FormGroup>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => closeDialog()} className="closeBtn">
						{strings.closeButton}
					</Button>
					<div className="actionBtnContainer">
						<ButtonPrimary
							data-testid="submitBtn"
							type="submit"
							className={!isValidForm() || isLoading ? "disabledBtn" : ""}
							onClick={() => submitCountryDetail()}
							disabled={!isValidForm()}
						>
							{strings.submitButton}
						</ButtonPrimary>
						{isLoading && (
							<CircularProgress
								size={24}
								className="btnLoader"
								color="primary"
							/>
						)}
					</div>
				</DialogActions>
			</Dialog>
		</div>
	);
};
